import { useEffect, useMemo, useState } from "react";

import { Button, Select, Spinner } from "@shapr3d/frontend-lib";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";

import { defaultTeamspaceName, queryKeys } from "../../../config";
import { TeamMembersQueryKey, useTeamMembers } from "../../../hooks/useTeams";
import { type TeamSpace, useTeamSpaces } from "../../../hooks/useTeamSpaces";
import type { TeamsUsersResponseData } from "../../../hooks/useTeamsTypes";
import { useTracker } from "../../../hooks/useTracker";
import useTranslation from "../../../hooks/useTranslation";
import { Modal } from "../../../lib/modal";
import { ElementClickAction, ElementClickLocation } from "../../../lib/TrackingAdapter";
import { UMSrequestClient } from "../../../stores/requestClient";

import style from "./AddToSpaceModal.module.css";

const useMutateAddToSpace = ({ onSuccess }: { onSuccess?: () => void }) => {
  const queryClient = useQueryClient();
  const { teamID } = useParams();

  return useMutation({
    onSuccess: (_, variables) => {
      queryClient.setQueryData<TeamsUsersResponseData | undefined>(
        [...TeamMembersQueryKey, teamID],
        (oldData) => {
          const updatedUsers = oldData?.users
            .filter((user) => variables.userIDs.includes(user.userId ?? ""))
            .map((user) => {
              return {
                ...user,
                spaces: [
                  ...(user.spaces?.filter((space) => space.id !== variables.selectedSpaceID) ?? []),
                  {
                    id: variables.selectedSpaceID ?? "",
                    name: variables.selectedSpaceName ?? undefined,
                  },
                ],
              };
            });

          if (oldData) {
            return {
              users: [
                ...oldData.users.filter((user) => !variables.userIDs.includes(user.userId ?? "")),
                ...(updatedUsers ?? []),
              ],
            };
          }
          return oldData;
        }
      );
      onSuccess?.();
    },
    mutationKey: [...queryKeys.authenticatedQueryKey, "add-to-teamspace"],
    mutationFn: ({
      selectedSpaceID,
      userIDs,
    }: {
      selectedSpaceID: string | undefined;
      selectedSpaceName: string | undefined;
      userIDs: string[];
    }) => {
      return UMSrequestClient.authenticatedRequest({
        method: "POST",
        url: `/teams/${teamID}/spaces/${selectedSpaceID}/users/add`,
        body: {
          users: userIDs.map((userID) => ({ id: userID })),
        },
      });
    },
  });
};

export const AddToSpaceModal = ({
  open,
  onClose,
  userIDs,
}: {
  open: boolean;
  onClose: () => void;
  userIDs: string[];
}) => {
  const { teamID } = useParams();
  const { data: spacesData } = useTeamSpaces(teamID);
  const tracker = useTracker();
  const { t, m } = useTranslation();

  const {
    mutate: mutateAddToSpace,
    isError,
    isPending,
  } = useMutateAddToSpace({ onSuccess: onClose });

  const { data: teamMembersData } = useTeamMembers(teamID);
  const selectedMembers = useMemo(() => {
    return teamMembersData?.users?.filter((member) => userIDs.includes(member.userId ?? ""));
  }, [userIDs, teamMembersData]);

  const filteredSpaces = useMemo(() => {
    return spacesData?.spaces.filter((space) => {
      return selectedMembers?.some((member) => {
        return !member.spaces?.some((memberSpace) => memberSpace?.id === space.id);
      });
    });
  }, [spacesData, selectedMembers]);

  const [selectedSpace, setSelectedSpace] = useState<TeamSpace | undefined>();
  useEffect(() => {
    setSelectedSpace(filteredSpaces?.[0]);
  }, [filteredSpaces, open]);

  useEffect(() => {
    if (!open) {
      setSelectedSpace(undefined);
    }
  }, [open]);

  return (
    <Modal
      title={t(m.MemberSpaceModal.AddTitle, { count: userIDs.length })}
      open={open}
      onClose={onClose}
      size="small"
      telemetryName="add_to_space"
    >
      {!filteredSpaces && <Spinner />}
      {!filteredSpaces && <div>{t(m.MemberSpaceModal.NoSpacesFound)}</div>}
      {filteredSpaces && (
        <div className={style.modalContentContainer}>
          <Select
            defaultValue={filteredSpaces?.[0]?.id}
            onChange={(e) => {
              setSelectedSpace(filteredSpaces.find((item) => item.id === e.target.value));
              tracker.trackButtonClick({
                action: ElementClickAction.SELECT_SPACE,
                details: e.target.value,
                location: ElementClickLocation.ADD_TO_SPACE_MODAL,
              });
            }}
          >
            {filteredSpaces.map(({ id, name }) => (
              <option key={id} value={id}>
                {name ?? defaultTeamspaceName}
              </option>
            ))}
          </Select>
          <Button
            disabled={!selectedSpace}
            loading={isPending}
            onClick={async () => {
              mutateAddToSpace({
                selectedSpaceID: selectedSpace?.id,
                selectedSpaceName: selectedSpace?.name,
                userIDs,
              });
              tracker.trackButtonClick({
                action: ElementClickAction.CONFIRM,
                location: ElementClickLocation.ADD_TO_SPACE_MODAL,
              });
            }}
          >
            {t(m.MemberSpaceModal.AddToSpace)}
          </Button>
          {isError && <div className={style.error}>{t(m.MemberSpaceModal.SomethingWentWrong)}</div>}
        </div>
      )}
    </Modal>
  );
};
